import { ending, nextCheck, SESSION_MS, type Ending } from './lifetime';

/**
 * Signing in with Google, and getting a token that can touch Drive.
 *
 * Two grants, because they are two different things and Google models them
 * separately. The ID token says *who you are* and is what the API verifies. The
 * access token is what reads and writes your scenarios, under the `drive.file`
 * scope — which only ever covers files this app created or you picked, so
 * granting it does not hand the app your Drive.
 *
 * Google's token is exchanged for one of this app's own, which is what makes a
 * sign-in last a working day: Google issues for about an hour and renewing that
 * silently needs One Tap, which is suppressed for reasons outside this app and
 * fails without saying so. The session token is presented on every request in
 * its place; the server verifies it locally and falls back to verifying a Google
 * token, so anything else holding one keeps working.
 *
 * It is kept in `localStorage` rather than `sessionStorage`, and that is the
 * consequence of asking for a day: a day spans closing the tab, and
 * `sessionStorage` does not. What makes that acceptable is the other half of the
 * rule — after ten hours, half an hour away ends it, so the token on disk stops
 * being useful long before it stops being valid. See `lifetime.ts`.
 *
 * Nothing is trusted on the strength of having been stored: a session past its
 * expiry is dropped rather than presented, and the server checks the signature
 * on every request regardless.
 */

/** The shape of the bits of Google Identity Services this file uses. */
interface GoogleIdentity {
  accounts: {
    id: {
      initialize(options: { client_id: string; callback: (response: { credential: string }) => void }): void;
      prompt(): void;
      renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
      disableAutoSelect(): void;
    };
    oauth2: {
      initTokenClient(options: {
        client_id: string;
        scope: string;
        /**
         * The account to ask about, so Google does not offer the chooser to
         * somebody who has already said who they are on the screen before.
         */
        hint?: string;
        callback: (response: { access_token?: string; expires_in?: number; error?: string }) => void;
      }): { requestAccessToken(options?: { prompt?: string }): void };
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentity;
  }
}

export const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

/**
 * Read the company directory, for profile photos.
 *
 * Asked for alongside Drive rather than separately, because two consent popups
 * in a row is one too many. It only ever exposes what the person can already see
 * in the directory, and it is what avoids the app holding an Admin SDK
 * credential that could impersonate anybody in the company.
 */
export const DIRECTORY_SCOPE = 'https://www.googleapis.com/auth/directory.readonly';
const SCRIPT = 'https://accounts.google.com/gsi/client';

interface Exchanged {
  token: string;
  expiresAt: number;
  name: string;
  email: string | null;
}

/**
 * Hand Google's sign-in to the API and get this app's own session back.
 *
 * Deliberately a plain `fetch` rather than going through `lib/api`: that module
 * imports this one for its headers, and there are no headers to send here — the
 * credential in the body is the whole request.
 */
async function exchange(credential: string): Promise<Exchanged | null> {
  const response = await fetch('/api/session', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  if (!response.ok) return null;
  return (await response.json()) as Exchanged;
}

export interface Session {
  /**
   * What every request carries. This app's own session where the server mints
   * them, and Google's ID token where it does not — the API takes either.
   */
  idToken: string;
  /** Passed through to Drive. Null until the scope has been granted. */
  driveToken: string | null;
  /**
   * When that token stops working — Google issues them for about an hour.
   *
   * Stored because nothing renews it behind the scenes any more: the app has to
   * know when to ask, and a token presented after it has lapsed is a save that
   * fails rather than a notice that asks.
   */
  driveExpiresAt: number | null;
  name: string;
  email: string | null;
  /** When this session was issued, and when it runs out regardless. */
  startedAt: number;
  expiresAt: number;
  /** The last time this person did anything; the idle rule reads it. */
  activeAt: number;
}

/** Bumped when the shape of what is stored changes; v1 had no lifetime on it. */
const STORAGE_KEY = 'tive.session.v2';

/**
 * When the ID token stops being worth presenting.
 *
 * Read from the token itself rather than counted from when we got it, because
 * a token restored from storage was issued at some point we no longer know.
 * A minute of slack, so a token that expires between the check and the request
 * is treated as already gone.
 */
function expiryOf(idToken: string): number | null {
  const claims = readClaims(idToken);
  return typeof claims.exp === 'number' ? claims.exp * 1000 - 60_000 : null;
}

function stored(): Session | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (typeof parsed.idToken !== 'string') return null;

    const now = Date.now();
    /*
     * Trust what was written, but not past the token itself: a stored
     * `expiresAt` is only a note about a signature, and the signature is what
     * the server will actually check.
     */
    const signed = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : null;
    const claimed = expiryOf(parsed.idToken);
    const expiresAt = Math.min(signed ?? Infinity, claimed ?? Infinity);

    const session: Session = {
      idToken: parsed.idToken,
      driveToken: typeof parsed.driveToken === 'string' ? parsed.driveToken : null,
      driveExpiresAt: typeof parsed.driveExpiresAt === 'number' ? parsed.driveExpiresAt : null,
      name: typeof parsed.name === 'string' ? parsed.name : 'Signed in',
      email: typeof parsed.email === 'string' ? parsed.email : null,
      startedAt: typeof parsed.startedAt === 'number' ? parsed.startedAt : now,
      expiresAt: Number.isFinite(expiresAt) ? expiresAt : now + SESSION_MS,
      activeAt: typeof parsed.activeAt === 'number' ? parsed.activeAt : now,
    };

    /*
     * Over is over, and the reason matters. A session that has run out or been
     * left alone too long is worse than none: it would be presented, refused,
     * and the sign-in screen would appear anyway with a failed request first.
     */
    if (ending(session, now) !== null) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return session;
  } catch {
    // Storage can be unavailable outright (private mode, a blocked origin), and
    // sitting on the sign-in screen is a better answer than not starting.
    return null;
  }
}

function remember(next: Session | null): void {
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing kept; the session still works for as long as the page lives */
  }
}

let lastNoted = 0;
let session: Session | null = stored();
const listeners = new Set<(session: Session | null) => void>();

export function currentSession(): Session | null {
  return session;
}

/** Why the session in hand is over, or null while it is not. */
export function sessionEnding(now: number = Date.now()): Ending {
  return session ? ending(session, now) : null;
}

/**
 * Note that somebody is still here.
 *
 * Written through to storage so a second tab does not look idle because the
 * work is happening in the first one. Throttled, because the alternative is a
 * JSON write on every mouse move.
 */
export function noteActivity(now: number = Date.now()): void {
  if (!session || now - lastNoted < 30_000) return;
  lastNoted = now;
  session = { ...session, activeAt: now };
  remember(session);
}

export function onSessionChange(listener: (session: Session | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setSession(next: Session | null): void {
  session = next;
  // A new session accepts the first note of activity straight away; the throttle
  // is about mouse moves within one session, not across two.
  lastNoted = 0;
  remember(next);
  for (const listener of listeners) listener(next);
}

/** Load Google's script once, and only when sign-in is actually required. */
let loading: Promise<GoogleIdentity> | null = null;

export function loadGoogle(): Promise<GoogleIdentity> {
  loading ??= new Promise<GoogleIdentity>((resolve, reject) => {
    if (window.google) {
      resolve(window.google);
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => {
      if (window.google) resolve(window.google);
      else reject(new Error('Google sign-in loaded but did not start.'));
    };
    script.onerror = () => reject(new Error('Could not reach Google to sign in.'));
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * What a JWT says about itself.
 *
 * Read here only to put a name in the corner of the screen. Nothing is trusted
 * on the strength of it — the API verifies the same token against Google's keys,
 * which is the check that counts.
 */
export function readClaims(idToken: string): { name?: string; email?: string; exp?: number } {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) return {};
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as { name?: string; email?: string; exp?: number };
  } catch {
    return {};
  }
}

/**
 * Put Google's own sign-in button on the page, and resolve when it is used.
 *
 * Google's rendered button rather than One Tap. One Tap is suppressed for all
 * sorts of reasons outside our control — the person dismissed it once before,
 * third-party cookies are off, the browser does not do FedCM — and when it is
 * suppressed it fails *silently*: no callback, no error, so a promise waiting on
 * it never settles and the screen sits on "Signing in…" for ever. A rendered
 * button always produces a click, and therefore always produces an answer.
 */
export async function renderSignIn(
  clientId: string,
  target: HTMLElement,
  options: { dark?: boolean } = {},
): Promise<Session> {
  const google = await loadGoogle();

  const idToken = await new Promise<string>((resolve, reject) => {
    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response.credential) resolve(response.credential);
        else reject(new Error('Google did not return a sign-in.'));
      },
    });
    google.accounts.id.renderButton(target, {
      // Google draws this button itself, so it cannot inherit the page's
      // colours -- told the wrong theme it is a white slab on a dark card.
      theme: options.dark ? 'filled_black' : 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'pill',
      width: 280,
    });
  });

  const claims = readClaims(idToken);
  const now = Date.now();
  /*
   * Trade it for one of ours, which is what makes the sign-in last the day.
   * A server that does not mint them says so, and the Google token is presented
   * directly instead — an hour rather than a day, but working.
   */
  const exchanged = await exchange(idToken).catch(() => null);

  const next: Session = {
    idToken: exchanged?.token ?? idToken,
    driveToken: null,
    driveExpiresAt: null,
    name: exchanged?.name ?? claims.name ?? claims.email ?? 'Signed in',
    email: exchanged?.email ?? claims.email ?? null,
    startedAt: now,
    expiresAt: exchanged?.expiresAt ?? expiryOf(idToken) ?? now + SESSION_MS,
    activeAt: now,
  };
  setSession(next);
  return next;
}

/**
 * How long to wait for Google to call back before giving up on it.
 *
 * A blocked popup is not an error anywhere: the window never opens, the
 * callback never fires, and the promise never settles. Without a ceiling that
 * is a spinner nobody can end — and on the hourly renewal it is a token that
 * quietly stops being refreshed.
 */
const GOOGLE_TIMEOUT_MS = 20_000;

/**
 * Settle a promise once, whichever answer arrives first.
 *
 * Both paths below race Google's callback against that timeout. Calling
 * `resolve` twice is silently ignored by the runtime, which would hide the
 * race rather than the bug; this makes "first one wins" something the code
 * says out loud.
 */
function once<T>(resolve: (value: T) => void, reject: (error: Error) => void) {
  let settled = false;
  return {
    ok(value: T) {
      if (settled) return;
      settled = true;
      resolve(value);
    },
    fail(error: Error) {
      if (settled) return;
      settled = true;
      reject(error);
    },
  };
}

/**
 * How long a Drive access token is treated as good for when Google does not say.
 * It issues for about an hour; short is the safe way to be wrong.
 */
const DRIVE_FALLBACK_MS = 50 * 60 * 1000;

/**
 * How early the app starts asking for a new one, so nothing is ever presented
 * after it has lapsed. A notice five minutes out is a click; a lapsed token is
 * a save that fails.
 */
export const DRIVE_GRACE_MS = 5 * 60 * 1000;

/**
 * Whether this browser has completed a Drive grant before.
 *
 * Not a credential — a boolean about whether the consent screen is worth
 * showing. Google's token client defaults to `select_account consent`, which
 * puts the account chooser and the consent screen in front of somebody who
 * approved Drive months ago; `prompt: ''` skips both but simply fails where
 * there is nothing to skip. So the first grant is asked for out loud and every
 * one after it quietly.
 */
const GRANTED_KEY = 'tive.drive-connected';

function knownGrant(): boolean {
  try {
    return window.localStorage.getItem(GRANTED_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberGrant(): void {
  try {
    window.localStorage.setItem(GRANTED_KEY, '1');
  } catch {
    /* the grant still works; the next connect just asks out loud */
  }
}

/**
 * Get a token that can touch Drive. **Only ever from a click.**
 *
 * That is the whole of what this file learned. Google's token client opens a
 * window — `prompt: ''` stops it showing anything, but it does not stop it
 * opening — and a window opened from a timer or a mount effect is one the
 * browser blocks. A blocked window never calls back, so the app waited twenty
 * seconds, gave up, and announced that Drive had been lost. That happened on
 * every sign-in and again every forty-five minutes, which is why it looked like
 * Drive itself was flaky.
 *
 * There is nothing to replace the timer with: Google has no silent refresh for
 * a browser-held token. Keeping one alive without asking needs a refresh token,
 * which means the authorisation-code flow, a client secret and somewhere on the
 * server to keep it. Until then the honest version is this — ask when there is
 * a hand on the mouse, and say so plainly the rest of the time.
 */
export async function connectDrive(clientId: string): Promise<string> {
  const google = await loadGoogle();
  const current = session;

  const granted = await new Promise<{ token: string; expiresIn: number }>((resolve, reject) => {
    const settle = once(resolve, reject);
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: `${DRIVE_SCOPE} ${DIRECTORY_SCOPE}`,
      // Whoever is already signed in, so Google does not offer the chooser to
      // somebody who said who they were on the screen before.
      ...(current?.email ? { hint: current.email } : {}),
      callback: (response) => {
        if (response.access_token) {
          settle.ok({ token: response.access_token, expiresIn: response.expires_in ?? 0 });
        } else {
          settle.fail(new Error(response.error ?? 'Google did not grant access to Drive.'));
        }
      },
    });

    /*
     * A window can still be blocked even from a click — a browser set to refuse
     * pop-ups outright will. It never calls back, so without a ceiling the
     * button reads "Waiting for Google…" until the tab is closed. Named for the
     * cause, because the fix is in the browser rather than anywhere here.
     */
    setTimeout(
      () =>
        settle.fail(
          new Error(
            'Google’s window did not open — it was probably blocked. Allow pop-ups for this site and try again.',
          ),
        ),
      GOOGLE_TIMEOUT_MS,
    );

    client.requestAccessToken(knownGrant() ? { prompt: '' } : undefined);
  });

  rememberGrant();
  const lifetime = granted.expiresIn > 0 ? granted.expiresIn * 1000 : DRIVE_FALLBACK_MS;
  if (session) {
    setSession({ ...session, driveToken: granted.token, driveExpiresAt: Date.now() + lifetime });
  }
  return granted.token;
}

/** Whether the Drive token is gone, or close enough to gone to ask about. */
export function driveExpiring(current: Session | null, now = Date.now()): boolean {
  if (!current) return false;
  if (!current.driveToken) return true;
  return current.driveExpiresAt !== null && current.driveExpiresAt - now <= DRIVE_GRACE_MS;
}

export function signOut(): void {
  window.google?.accounts.id.disableAutoSelect();
  setSession(null);
}

/**
 * Drop a session the API has refused.
 *
 * Not `signOut`: that also tells Google not to sign this person in again
 * automatically, which is right when they asked to leave and wrong when their
 * token simply aged out. Here the answer is to ask again, so auto-select stays
 * on and the sign-in screen is usually one click.
 */
export function expireSession(): void {
  if (session) setSession(null);
}

/** The headers a request carries, if there is a session to carry them from. */
export function authHeaders(): Record<string, string> {
  if (!session) return {};
  return {
    authorization: `Bearer ${session.idToken}`,
    ...(session.driveToken ? { 'x-drive-token': session.driveToken } : {}),
  };
}

/**
 * Watch the clock and the person, and end the session when either says so.
 *
 * Both halves are here rather than in a component because they outlive any one
 * screen: the rules are about the session, and a component that unmounted would
 * take the timer with it.
 *
 * Returns a teardown, and reports *why* it ended — "your session expired" and
 * "you were signed out after being away" are different things to be told.
 */
export function watchSession(options: {
  clientId: string;
  onEnded: (reason: Ending) => void;
  onDriveLost: () => void;
}): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const activity = () => noteActivity();
  for (const type of ['pointerdown', 'keydown', 'wheel'] as const) {
    window.addEventListener(type, activity, { passive: true });
  }
  const onVisible = () => {
    if (document.visibilityState === 'visible') noteActivity();
  };
  document.addEventListener('visibilitychange', onVisible);

  const tick = () => {
    if (stopped) return;
    const current = currentSession();
    if (!current) return;

    const reason = ending(current);
    if (reason) {
      signOut();
      options.onEnded(reason);
      return;
    }
    timer = setTimeout(tick, nextCheck(current));
  };
  tick();

  /*
   * Watched rather than renewed.
   *
   * This used to call Google on a timer to get a fresh token before the old one
   * lapsed. It cannot: Google's token client opens a window, and a window
   * opened from a timer is blocked — so every renewal silently failed, timed
   * out after twenty seconds, and reported Drive as lost. Which it then was.
   *
   * So the clock is only read here, and the asking is left to a person with a
   * hand on the mouse. Five minutes of warning is enough to click a notice, and
   * means nothing is ever presented to Drive after it has stopped working.
   */
  const driveTimer = setInterval(() => {
    if (!currentSession()) return;
    if (driveExpiring(currentSession())) options.onDriveLost();
  }, 30_000);

  return () => {
    stopped = true;
    clearTimeout(timer);
    clearInterval(driveTimer);
    for (const type of ['pointerdown', 'keydown', 'wheel'] as const) {
      window.removeEventListener(type, activity);
    }
    document.removeEventListener('visibilitychange', onVisible);
  };
}
